import { Component, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, Skeleton } from "@/design-system/components/ui";

/**
 * The ONE shared widget chrome + degraded/empty/loading pattern for every
 * dashboard widget (recon §8): a failing widget renders a quiet degraded
 * state and never breaks the page; a zero renders a teaching empty state,
 * never a broken-looking blank.
 */
export interface WidgetFrameProps {
  title: string;
  subtitle?: string | null;
  /** Optional "View all" destination. */
  to?: string;
  toLabel?: string;
  isLoading?: boolean;
  isError?: boolean;
  /** When true (and not loading/error), renders the teaching empty state. */
  isEmpty?: boolean;
  emptyText?: string;
  children?: ReactNode;
}

export function WidgetFrame({
  title,
  subtitle,
  to,
  toLabel = "View all",
  isLoading,
  isError,
  isEmpty,
  emptyText = "Nothing needs you here right now.",
  children,
}: WidgetFrameProps) {
  return (
    <Card className="h-full">
      <div className="flex items-start justify-between gap-space-2 mb-space-3">
        <div className="min-w-0">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid truncate">
            {title}
          </div>
          {subtitle && <div className="text-label text-gray-mid mt-space-1">{subtitle}</div>}
        </div>
        {to && !isError && (
          <Link
            to={to}
            className="inline-flex items-center gap-space-1 text-label font-display uppercase tracking-label text-gold-shadow hover:text-onyx shrink-0 whitespace-nowrap"
          >
            {toLabel} <ArrowRight size={12} strokeWidth={2} aria-hidden />
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-space-2" aria-busy>
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
        </div>
      ) : isError ? (
        <WidgetDegraded />
      ) : isEmpty ? (
        <div className="text-small text-gray-mid">
          {emptyText}
          {to && (
            <>
              {" "}
              <Link to={to} className="text-gold-shadow hover:text-onyx underline">
                {toLabel}
              </Link>
            </>
          )}
        </div>
      ) : (
        children
      )}
    </Card>
  );
}

function WidgetDegraded() {
  return (
    <div className="text-small text-gray-mid">
      This widget couldn't load right now. The rest of your dashboard is unaffected — try again
      shortly.
    </div>
  );
}

/**
 * Per-widget error boundary: a render crash inside one widget degrades that
 * card quietly instead of unmounting the whole dashboard.
 */
export class WidgetBoundary extends Component<
  { title: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <WidgetFrame title={this.props.title} isError />
      );
    }
    return this.props.children;
  }
}
